import type { ReactNode } from "react";
import { FormattedMessage } from "react-intl";
import type { DropzoneState } from "react-dropzone";
import classnames from "classnames";

import Icon from "common/core/icon";
import Button, { type ButtonPropsOmit } from "common/core/button";

import Styles from "./index.module.scss";

type Props = {
  label: ReactNode;
  dropzoneState: DropzoneState;
  disabled: boolean;
};
type UploadButtonProps = ButtonPropsOmit<"onClick"> & { dropzoneState: DropzoneState };

const getActiveStateClass = (dropzoneState: DropzoneState, disabled: boolean) => {
  const { isDragActive, isFocused } = dropzoneState;

  if (disabled) {
    return Styles.disabled;
  }
  if (isDragActive) {
    return Styles.dragActive;
  }
  if (isFocused) {
    return Styles.dragFocused;
  }
};

export function Dropzone({ label, dropzoneState, disabled }: Props) {
  const { getInputProps, getRootProps } = dropzoneState;

  const className = classnames(Styles.dropzone, getActiveStateClass(dropzoneState, disabled));

  return (
    <div
      {...getRootProps({
        className,
      })}
    >
      <input data-automation-id="uploader-input" {...getInputProps()} />
      <div className={Styles.uploadIcon}>
        <Icon className="icon" name="upload-2" />
      </div>
      <div className={Styles.header}>{label}</div>
      <div className={Styles.subheader}>
        <FormattedMessage
          id="57817dcd-0c9d-4f4a-979b-e51f8e2b5b18"
          defaultMessage="Drop files here or click to browse."
        />
      </div>
    </div>
  );
}

export function DropzoneImagePreview({
  image,
  showBoundingBox,
}: {
  image: {
    src: string;
    alt: string;
  };
  showBoundingBox: boolean;
}) {
  const { alt, src } = image;
  return (
    <div
      className={classnames(Styles.imagePreview, {
        [Styles.imagePreviewClear]: !showBoundingBox,
      })}
    >
      <img
        data-automation-id="dropzone-preview"
        className={classnames(Styles.previewImage, {
          [Styles.boundingBox]: showBoundingBox,
        })}
        src={src}
        alt={alt}
        onLoad={() => {
          URL.revokeObjectURL(src);
        }}
      />
    </div>
  );
}

export function UploadButton(props: UploadButtonProps) {
  const { dropzoneState, ...buttonProps } = props;

  return (
    <div
      {...dropzoneState.getRootProps({
        className: Styles.uploadButton,
      })}
    >
      <Button {...buttonProps} onClick={() => {}} />
      <input data-automation-id="uploader-input" {...dropzoneState.getInputProps()} />
    </div>
  );
}
